import {useEffect, useState} from 'react';

import {InstrumentServiceDetails} from 'instruments/connections/ConnectionConstants';
import * as ConnectionDAO from 'instruments/connections/ConnectionDAO';
import notifications from 'notifications';

import {getOnDemandRetrievalKind, OnDemandRetrievalKind, RetrievalMode} from './onDemandRetrieval';

export interface OnDemandConnection {
  connectionId: string;
  instrumentServiceDetails: InstrumentServiceDetails;
  retrievalKind: OnDemandRetrievalKind;
}

export interface ConnectionRetrievalMode {
  // The selected connection, when exactly one is selected.
  connectionId: string | null;
  isLoadingDetails: boolean;
  didDetailsFailToLoad: boolean;
  // The selected connection, if it can retrieve data on demand.
  onDemandConnection: OnDemandConnection | null;
  mode: RetrievalMode;
  setMode: (mode: RetrievalMode) => void;
}

// Stored with the connection they were loaded for, so that on the render where the selection changes (before the
// effect below clears them) they aren't used for the newly selected connection. Until they're cleared, the chosen
// mode doesn't apply either, since on-demand retrieval is unavailable without details.
type LoadedDetails = {connectionId: string} & (
  | {instrumentServiceDetails: InstrumentServiceDetails; didFail: false}
  | {instrumentServiceDetails: null; didFail: true}
);

/**
 * Tracks whether the connection files picker shows files the connection has synced or retrieves them on
 * demand. On-demand retrieval needs exactly one selected connection that supports it, and is the default for
 * connections that don't sync their files to Benchling.
 */
export default function useConnectionRetrievalMode(
  selectedConnectionIds: Array<string>
): ConnectionRetrievalMode {
  const connectionId = selectedConnectionIds.length === 1 ? selectedConnectionIds[0] : null;
  const [loadedDetails, setLoadedDetails] = useState<LoadedDetails | null>(null);
  const [chosenMode, setChosenMode] = useState<RetrievalMode | null>(null);

  useEffect(() => {
    setLoadedDetails(null);
    setChosenMode(null);
    if (connectionId == null) {
      return undefined;
    }
    let cancelled = false;
    ConnectionDAO.getInstrumentServiceDetails(connectionId).then(
      (instrumentServiceDetails) => {
        if (!cancelled) {
          setLoadedDetails({connectionId, instrumentServiceDetails, didFail: false});
        }
      },
      () => {
        if (!cancelled) {
          setLoadedDetails({connectionId, instrumentServiceDetails: null, didFail: true});
          notifications.error('Unable to load connection details. Please try again.');
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [connectionId]);

  const currentDetails = loadedDetails?.connectionId === connectionId ? loadedDetails : null;
  const instrumentServiceDetails = currentDetails?.instrumentServiceDetails ?? null;
  const retrievalKind =
    instrumentServiceDetails == null ? null : getOnDemandRetrievalKind(instrumentServiceDetails);
  const onDemandConnection =
    connectionId == null || instrumentServiceDetails == null || retrievalKind == null
      ? null
      : {connectionId, instrumentServiceDetails, retrievalKind};

  const defaultMode = instrumentServiceDetails?.isFileSyncing
    ? RetrievalMode.SYNCED
    : RetrievalMode.ON_DEMAND;
  const mode = onDemandConnection == null ? RetrievalMode.SYNCED : (chosenMode ?? defaultMode);

  return {
    connectionId,
    isLoadingDetails: connectionId != null && currentDetails == null,
    didDetailsFailToLoad: currentDetails?.didFail ?? false,
    onDemandConnection,
    mode,
    setMode: setChosenMode,
  };
}
